import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import { FaArrowLeft, FaCalendarAlt, FaChartPie, FaCheck, FaCrown, FaDownload, FaKey, FaRocket, FaShieldAlt, FaSignOutAlt, FaStar, FaTachometerAlt, FaUser, FaUsers } from 'react-icons/fa';
import AdminUsoAnalytics from './AdminUsoAnalytics';
import AdminVisaoGeral from './AdminVisaoGeral';
import { isVisitEvent, planLabel, resolveScreen } from '../config/adminUsageAnalytics';
import * as XLSX from 'xlsx';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import packageJson from '../../package.json';
import { PlanService } from '../services/PlanService';
import { PAYMENT_CATALOG } from '../config/paymentCatalog';
import {
  MODULE_PERMISSION_CATALOG,
  getPlanIncludedModules,
  isModuleIncludedInPlan,
  isModuleAllowed,
  permissionMutationFor,
} from '../config/modulePermissions';
import './AdminPanel.css';

const REVENUE_PLAN_IDS = ['essential', 'professional', 'management'];
const NEW_COMMERCIAL_PLAN_IDS = ['essential', 'professional', 'management'];

function AdminPanel({ onBack, currentUser = null }) {
  const VERSAO_ATUAL = packageJson.version; // Versão atual do app
  // Estados principais
  const [aba, setAba] = useState('dashboard');
  const [eventos, setEventos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loadingId, setLoadingId] = useState(null);
  const [filtroPeriodo, setFiltroPeriodo] = useState("7");
  const [paginaUsuarios, setPaginaUsuarios] = useState(1);
  const itensPorPagina = 15;
  
  // Estados para filtros de usuários
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [filtroAdmin, setFiltroAdmin] = useState("todos");
  const [buscaUsuario, setBuscaUsuario] = useState("");
  
  // Estados para permissões de módulos
  const [usuarioSelecionado, setUsuarioSelecionado] = useState(null);
  const [userModulos, setUserModulos] = useState([]);
  const [loadingPerm, setLoadingPerm] = useState(false);
  
  // Estados para gestão de planos
  const [usuarioPlanoSelecionado, setUsuarioPlanoSelecionado] = useState(null);
  const [irrigacaoInfo, setIrrigacaoInfo] = useState(null); // { ativo, expirado, expiracao, trialUsado }
  const [loadingIrrigacao, setLoadingIrrigacao] = useState(false);
  const [sateliteInfo, setSateliteInfo] = useState(null); // { ativo, expirado, expiracao, trialUsado }
  const [loadingSatelite, setLoadingSatelite] = useState(false);
  const [loadingPlano, setLoadingPlano] = useState(false);
  const [planosConfig, setPlanosConfig] = useState([]);
  const [historicoPlanos, setHistoricoPlanos] = useState([]);
  const [filtroPlano, setFiltroPlano] = useState("todos");
  const [loadingDados, setLoadingDados] = useState(false);
  const currentUserIsOwner = currentUser?.is_owner === true;
  
  const modulosDisponiveis = MODULE_PERMISSION_CATALOG.map(item => ({
    nome: item.key,
    label: item.label,
  }));

  const usuariosFiltradosPermissoes = useMemo(() => {
    const termo = buscaUsuario.trim().toLowerCase();
    if (!termo) return usuarios;
    return usuarios.filter(u => (
      u.nome?.toLowerCase().includes(termo) || u.email?.toLowerCase().includes(termo)
    ));
  }, [buscaUsuario, usuarios]);

  // Label do período selecionado
  const labelPeriodo = filtroPeriodo === "todos" ? "Todo o período" : `Últimos ${filtroPeriodo} dias`;

  // Data limite memoizada para evitar inconsistências durante o render
  const dataLimitePeriodo = useMemo(() => {
    if (filtroPeriodo === "todos") return null;
    const d = new Date();
    d.setDate(d.getDate() - parseInt(filtroPeriodo));
    d.setHours(0, 0, 0, 0);
    return d;
  }, [filtroPeriodo]);

  // Filtrar por período
  const filtrarPorPeriodo = (data) => {
    if (!dataLimitePeriodo) return true;
    if (!data) return false;
    return new Date(data) >= dataLimitePeriodo;
  };

  // IDs dos admins (excluídos das estatísticas)
  const adminIds = useMemo(() => new Set(usuarios.filter(u => u.is_admin).map(u => u.id)), [usuarios]);
  const usuariosNaoAdmin = useMemo(() => usuarios.filter(u => !u.is_admin), [usuarios]);

  // Eventos filtrados (excluindo admins)
  const eventosFiltrados = useMemo(() => {
    return eventos.filter(ev => filtrarPorPeriodo(ev.data_hora) && !adminIds.has(ev.user_id));
  }, [eventos, filtroPeriodo, adminIds]);

  // Buscar últimos acessos dos usuários
  const ultimosAcessos = useMemo(() => {
    const acessMap = {};
    eventosFiltrados.forEach(ev => {
      if (!ev.user_id) return;
      if (!acessMap[ev.user_id] || new Date(ev.data_hora) > new Date(acessMap[ev.user_id])) {
        acessMap[ev.user_id] = ev.data_hora;
      }
    });
    return acessMap;
  }, [eventosFiltrados]);

  // Versão do app de cada usuário (última detectada)
  const versoesPorUsuario = useMemo(() => {
    const versaoMap = {};
    eventosFiltrados.forEach(ev => {
      if (!ev.user_id || !ev.detalhes?.app_version) return;
      if (!versaoMap[ev.user_id] || new Date(ev.data_hora) > new Date(versaoMap[ev.user_id].data)) {
        versaoMap[ev.user_id] = {
          versao: ev.detalhes.app_version,
          data: ev.data_hora
        };
      }
    });
    return versaoMap;
  }, [eventosFiltrados]);

  // Total de visitas por usuário (eventos de saída não contam)
  const totalAcessosPorUsuario = useMemo(() => {
    const acessMap = {};
    eventosFiltrados.forEach(ev => {
      if (!ev.user_id || !isVisitEvent(ev)) return;
      acessMap[ev.user_id] = (acessMap[ev.user_id] || 0) + 1;
    });
    return acessMap;
  }, [eventosFiltrados]);

  // Buscar módulos do usuário selecionado
  useEffect(() => {
    if (!usuarioSelecionado || aba !== 'permissoesModulos') return;
    async function fetchUserModulos() {
      setLoadingPerm(true);
      // Busca módulos gerais
      const { data, error } = await supabase.from('user_modules').select('module_name').eq('user_id', usuarioSelecionado.id);
      if (error) console.error('Erro ao buscar módulos do usuário:', error);
      const modulos = data ? data.map(d => d.module_name) : [];

      setUserModulos(modulos);
      setLoadingPerm(false);
    }
    fetchUserModulos();
  }, [usuarioSelecionado, aba]);

  // Alternar permissão de módulo
  async function handleToggleModulo(moduloName, checked) {
    setLoadingPerm(true);
    try {
      if (usuarioSelecionado.is_owner) throw new Error('As permissões do proprietário não podem ser restringidas.');
      const mutation = permissionMutationFor(moduloName, checked, usuarioSelecionado.plano);
      const { error } = await supabase.rpc('admin_set_user_module_access', {
        p_user_id: usuarioSelecionado.id,
        p_module_name: moduloName,
        p_allowed: checked,
      });
      if (error) throw error;

      setUserModulos(prev => {
        const next = prev.filter(name => ![moduloName, `deny:${moduloName}`].includes(name));
        return mutation.insert ? [...next, mutation.insert] : next;
      });
    } catch (error) {
      console.error('Erro ao atualizar permissão do módulo:', error);
      alert('Não foi possível atualizar esta permissão. Nenhuma outra configuração foi alterada.');
    } finally {
      setLoadingPerm(false);
    }
  }

  // Toggle Admin
  async function toggleAdmin(userId, isAdmin) {
    const target = usuarios.find(item => item.id === userId);
    if (!currentUserIsOwner) {
      alert('Somente o proprietário da plataforma pode alterar administradores.');
      return;
    }
    if (target?.is_owner) {
      alert('A conta proprietária não pode perder o acesso administrativo.');
      return;
    }
    setLoadingId(userId);
    const { error } = await supabase.rpc('admin_definir_usuario_admin', {
      p_user_id: userId,
      p_is_admin: !isAdmin,
    });
    
    if (error) {
      alert('Erro ao alterar privilégio: ' + error.message);
    } else {
      setUsuarios(prev => prev.map(u => u.id === userId ? { ...u, is_admin: !isAdmin } : u));
    }
    setLoadingId(null);
  }

  async function handleToggleAccountAccess(user) {
    if (!currentUserIsOwner) {
      alert('Somente o proprietário da plataforma pode bloquear ou reativar contas.');
      return;
    }
    if (user.is_owner) {
      alert('A conta proprietária não pode ser bloqueada.');
      return;
    }

    const shouldBlock = user.account_status !== 'blocked';
    const actionLabel = shouldBlock ? 'bloquear o acesso' : 'reativar o acesso';
    if (!window.confirm(`Tem certeza que deseja ${actionLabel} de ${user.nome || user.email}?`)) return;

    let reason = '';
    if (shouldBlock) {
      reason = window.prompt('Motivo do bloqueio (opcional):', '') || '';
    }

    const userId = user.id;
    setLoadingId(userId);
    try {
      const { data, error } = await supabase.functions.invoke('admin-user-access', {
        body: { userId, blocked: shouldBlock, reason },
      });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Operação não confirmada pelo servidor');

      const accountStatus = shouldBlock ? 'blocked' : 'active';
      setUsuarios(prev => prev.map(item => item.id === userId
        ? {
            ...item,
            account_status: accountStatus,
            blocked_reason: shouldBlock ? reason : null,
          }
        : item));
      alert(shouldBlock
        ? 'Acesso bloqueado. Novos logins e renovações de sessão foram interrompidos.'
        : 'Acesso reativado com sucesso.');
    } catch (error) {
      alert('Não foi possível atualizar o acesso: ' + (error?.message || 'erro desconhecido'));
    } finally {
      setLoadingId(null);
    }
  }

  async function handleExtendTrial(userId, days) {
    if (!window.confirm(`Adicionar ${days} dias ao teste grátis deste usuário?`)) return;
    setLoadingPlano(true);
    try {
      const { data, error } = await supabase.rpc('admin_estender_trial_usuario', {
        p_user_id: userId,
        p_dias: days,
      });
      if (error) throw error;

      const updateData = { plano_trial: true, plano_data_expiracao: data.expira_em };
      setUsuarios(prev => prev.map(item => item.id === userId ? { ...item, ...updateData } : item));
      setUsuarioPlanoSelecionado(prev => prev?.id === userId ? { ...prev, ...updateData } : prev);
      alert(`Teste grátis estendido até ${new Date(data.expira_em).toLocaleDateString('pt-BR')}.`);
    } catch (error) {
      alert('Não foi possível estender o teste grátis: ' + error.message);
    } finally {
      setLoadingPlano(false);
    }
  }

  // ========== FUNÇÕES DE GESTÃO DE PLANOS ==========
  
  // Carregar configurações de planos
  useEffect(() => {
    async function fetchPlanosConfig() {
      const result = await PlanService.getPlanos();
      if (result.success) {
        setPlanosConfig(result.data || []);
      }
    }
    fetchPlanosConfig();
  }, []);

  // Carregar histórico e addons do usuário selecionado
  useEffect(() => {
    if (!usuarioPlanoSelecionado) {
      setHistoricoPlanos([]);
      setIrrigacaoInfo(null);
      setSateliteInfo(null);
      return;
    }

    async function fetchHistorico() {
      const { data, error } = await supabase
        .from('historico_planos')
        .select('*')
        .eq('user_id', usuarioPlanoSelecionado.id)
        .order('data_mudanca', { ascending: false })
        .limit(10);
      
      if (!error) setHistoricoPlanos(data || []);
    }

    async function fetchIrrigacao() {
      const { data } = await supabase
        .from('usuarios')
        .select('addon_irrigacao, addon_irrigacao_expiracao, addon_irrigacao_trial_usado, addon_irrigacao_inicio')
        .eq('id', usuarioPlanoSelecionado.id)
        .single();
      if (!data) { setIrrigacaoInfo(null); return; }
      const agora = new Date();
      const exp = data.addon_irrigacao_expiracao ? new Date(data.addon_irrigacao_expiracao) : null;
      const ativo = data.addon_irrigacao && exp && exp > agora;
      setIrrigacaoInfo({
        ativo: ativo || false,
        expirado: data.addon_irrigacao && exp && exp <= agora,
        expiracao: exp,
        trialUsado: data.addon_irrigacao_trial_usado || false
      });
    }

    async function fetchSatelite() {
      try {
        const { data, error } = await supabase
          .from('usuarios')
          .select('addon_satelite, addon_satelite_expiracao, addon_satelite_trial_usado, addon_satelite_inicio')
          .eq('id', usuarioPlanoSelecionado.id)
          .single();
        if (error || !data) {
          setSateliteInfo({ ativo: false, expirado: false, expiracao: null, trialUsado: false });
          return;
        }
        const agora = new Date();
        const exp = data.addon_satelite_expiracao ? new Date(data.addon_satelite_expiracao) : null;
        const ativo = data.addon_satelite && exp && exp > agora;
        setSateliteInfo({
          ativo: ativo || false,
          expirado: data.addon_satelite && exp && exp <= agora,
          expiracao: exp,
          trialUsado: data.addon_satelite_trial_usado || false
        });
      } catch (err) {
        setSateliteInfo({ ativo: false, expirado: false, expiracao: null, trialUsado: false });
      }
    }

    fetchHistorico();
    fetchIrrigacao();
    fetchSatelite();
  }, [usuarioPlanoSelecionado]);

  // Ativar/desativar addon irrigação (ADMIN)
  async function handleToggleAddonIrrigacao(ativar, dias = null) {
    setLoadingIrrigacao(true);
    try {
      const { data, error } = await supabase.rpc('admin_toggle_addon_irrigacao', {
        p_target_user_id: usuarioPlanoSelecionado.id,
        p_ativar: ativar,
        p_dias: ativar ? dias : null
      });
      if (error) throw error;
      if (!data?.sucesso) throw new Error('Operação não confirmada pelo servidor');

      if (ativar) {
        const expiracao = new Date(data.expiracao);
        setIrrigacaoInfo({ ativo: true, expirado: false, expiracao, trialUsado: irrigacaoInfo?.trialUsado || false });
        alert(`✅ Add-on Irrigação ativado!\nExpira em: ${expiracao.toLocaleDateString('pt-BR')}`);
      } else {
        setIrrigacaoInfo(prev => ({ ...prev, ativo: false, expirado: false, expiracao: null }));
        alert('✅ Add-on Irrigação desativado.');
      }
    } catch (err) {
      alert('Erro: ' + err.message);
    } finally {
      setLoadingIrrigacao(false);
    }
  }

  // Ativar/desativar addon satélite (ADMIN)
  async function handleToggleAddonSatelite(ativar, dias = null) {
    setLoadingSatelite(true);
    try {
      const { data, error } = await supabase.rpc('admin_toggle_addon_satelite', {
        p_target_user_id: usuarioPlanoSelecionado.id,
        p_ativar: ativar,
        p_dias: ativar ? dias : null
      });
      if (error) throw error;
      if (!data?.sucesso) throw new Error('Operação não confirmada pelo servidor');

      if (ativar) {
        const expiracao = new Date(data.expiracao);
        setSateliteInfo({ ativo: true, expirado: false, expiracao, trialUsado: sateliteInfo?.trialUsado || false });
        alert(`✅ Add-on Satélite ativado!\nExpira em: ${expiracao.toLocaleDateString('pt-BR')}`);
      } else {
        setSateliteInfo(prev => ({ ...prev, ativo: false, expirado: false, expiracao: null }));
        alert('✅ Add-on Satélite desativado.');
      }
    } catch (err) {
      alert('Erro: ' + err.message);
    } finally {
      setLoadingSatelite(false);
    }
  }

  async function handleRenewOwnPlan(periodicidade) {
    if (!usuarioPlanoSelecionado || String(usuarioPlanoSelecionado.id) !== String(currentUser?.id)) {
      alert('A renovação própria só pode ser feita para a conta autenticada.');
      return;
    }
    const periodoLabel = periodicidade === 'anual' ? 'anual' : 'mensal';
    if (!window.confirm(`Renovar seu plano atual por mais um período ${periodoLabel}?`)) return;

    setLoadingPlano(true);
    try {
      const { data, error } = await supabase.rpc('admin_renovar_plano_proprio', {
        p_periodicidade: periodicidade,
      });
      if (error) throw error;
      if (!data?.success) throw new Error('Renovação não confirmada pelo servidor.');

      const { data: usuarioAtualizado, error: errorUsuario } = await supabase
        .from('usuarios')
        .select('*')
        .eq('id', currentUser.id)
        .single();
      if (errorUsuario) throw errorUsuario;

      setUsuarios(prev => prev.map(u => u.id === currentUser.id ? usuarioAtualizado : u));
      setUsuarioPlanoSelecionado(usuarioAtualizado);
      alert(`✅ Plano renovado até ${new Date(usuarioAtualizado.plano_data_expiracao).toLocaleDateString('pt-BR')}.`);
    } catch (error) {
      console.error('Erro ao renovar o próprio plano:', error);
      alert('❌ Erro ao renovar plano: ' + error.message);
    } finally {
      setLoadingPlano(false);
    }
  }

  // Alterar plano de um usuário (ADMIN)
  async function handleChangePlan(userId, novoPlano, diasTrial = null, periodicidade = null) {
    const target = usuarios.find(item => item.id === userId);
    if (target?.is_owner) {
      alert('O plano da conta proprietária é protegido.');
      return;
    }
    const mensagem = diasTrial 
      ? `Ativar ${novoPlano.toUpperCase()} TRIAL (${diasTrial} dias) para este usuário?`
      : periodicidade
        ? `Ativar ${novoPlano.toUpperCase()} (${periodicidade}) para este usuário?`
        : `Alterar plano para ${novoPlano.toUpperCase()}?`;
    
    if (!window.confirm(mensagem)) return;
    
    setLoadingPlano(true);
    
    try {
      if (diasTrial) {
        const { data: trialResult, error } = await supabase.rpc('admin_conceder_trial_plano', {
          p_user_id: userId,
          p_plano_novo: novoPlano,
          p_dias: diasTrial,
        });
        if (error) throw error;

        const updateData = {
          plano: novoPlano,
          plano_data_inicio: new Date().toISOString(),
          plano_data_expiracao: trialResult.expira_em,
          plano_trial: true,
          plano_trial_usado: true,
          plano_periodicidade: null,
        };

        // Atualiza estado
        setUsuarios(prev => prev.map(u => u.id === userId ? { ...u, ...updateData } : u));
        if (usuarioPlanoSelecionado?.id === userId) {
          setUsuarioPlanoSelecionado(prev => ({ ...prev, ...updateData }));
        }

        alert(`✅ Trial de ${diasTrial} dias ativado com sucesso!`);

      } else {
        // PLANO PERMANENTE: Usa a função SQL que calcula tudo automaticamente
        const { data, error } = await supabase.rpc('admin_registrar_mudanca_plano', {
          p_user_id: userId,
          p_plano_novo: novoPlano,
          p_periodicidade: periodicidade
        });

        if (error) throw error;

        // Busca os dados atualizados do usuário
        const { data: usuarioAtualizado, error: errorUsuario } = await supabase
          .from('usuarios')
          .select('*')
          .eq('id', userId)
          .single();

        if (errorUsuario) throw errorUsuario;

        // Atualiza estado
        setUsuarios(prev => prev.map(u => u.id === userId ? usuarioAtualizado : u));
        if (usuarioPlanoSelecionado?.id === userId) {
          setUsuarioPlanoSelecionado(usuarioAtualizado);
        }

        const msg = periodicidade 
          ? `✅ Plano ${novoPlano.toUpperCase()} (${periodicidade}) ativado!\n\nExpira em: ${new Date(usuarioAtualizado.plano_data_expiracao).toLocaleDateString('pt-BR')}`
          : `✅ Plano ${novoPlano.toUpperCase()} ativado com sucesso!`;
        
        alert(msg);
      }
      
      // Recarrega histórico
      if (usuarioPlanoSelecionado?.id === userId) {
        const { data } = await supabase
          .from('historico_planos')
          .select('*')
          .eq('user_id', userId)
          .order('data_mudanca', { ascending: false })
          .limit(10);
        setHistoricoPlanos(data || []);
      }
    } catch (error) {
      console.error('Erro ao alterar plano:', error);
      alert('❌ Erro ao alterar plano: ' + error.message);
    } finally {
      setLoadingPlano(false);
    }
  }

  // Estatísticas de planos
  const estatisticasPlanos = useMemo(() => {
    const stats = {
      sensor_free: 0,
      starter: 0,
      premium: 0,
      pro: 0,
      essential: 0,
      professional: 0,
      management: 0,
      trial: 0
    };

    usuarios.filter(u => !u.is_admin).forEach(u => {
      const plano = u.plano || 'starter';
      stats[plano] = (stats[plano] || 0) + 1;
      if (u.plano_trial) stats.trial++;
    });

    return stats;
  }, [usuarios]);

  // Exportar para XLSX
  async function exportarUsuarios() {
    const dadosExport = usuariosFiltrados.map(u => {
      return {
        'Nome': u.nome || '',
        'Email': u.email,
        'Plano': (u.plano || 'starter').toUpperCase() + (u.plano_trial ? ' (Trial)' : ''),
        'Plano Expira': u.plano_data_expiracao ? new Date(u.plano_data_expiracao).toLocaleDateString('pt-BR') : '-',
        'Admin': u.is_admin ? 'Sim' : 'Não',
        'Status': ultimosAcessos[u.id] ? 'Ativo' : 'Inativo',
        'Versão App': versoesPorUsuario[u.id]?.versao || '-',
        'Data Cadastro': u.criado_em ? new Date(u.criado_em).toLocaleString('pt-BR') : '',
        'Último Acesso': ultimosAcessos[u.id] ? new Date(ultimosAcessos[u.id]).toLocaleString('pt-BR') : 'Nunca',
        'Total Acessos': totalAcessosPorUsuario[u.id] || 0
      };
    });
    
    const ws = XLSX.utils.json_to_sheet(dadosExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Usuarios');
    await salvarPlanilha(wb, `usuarios_${new Date().toISOString().split('T')[0]}.xlsx`);
  }

  async function exportarUsoUsuarios(linhas) {
    const dadosExport = linhas.map(r => ({
      'Nome': r.nome || '',
      'Email': r.email || '',
      'Plano': r.plano.toUpperCase() + (r.trial ? ' (Teste)' : ''),
      'Segmento': r.segment,
      'Visitas': r.visits,
      'Dias ativos': r.activeDays,
      'Módulos usados': r.modules,
      'Tela principal': r.topScreen,
      'Tempo total (min)': Math.round(r.seconds / 60),
      'Último acesso': r.last ? new Date(r.last).toLocaleString('pt-BR') : 'Sem acesso no período',
      'Período': labelPeriodo,
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(dadosExport), 'Uso');
    await salvarPlanilha(wb, `uso_usuarios_${new Date().toISOString().split('T')[0]}.xlsx`);
  }

  async function salvarPlanilha(wb, fileName) {
    if (Capacitor.isNativePlatform()) {
      try {
        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const base64 = btoa(
          new Uint8Array(wbout).reduce((data, byte) => data + String.fromCharCode(byte), '')
        );
        await Filesystem.writeFile({
          path: fileName,
          data: base64,
          directory: Directory.Documents
        });
        alert(`Arquivo salvo em: Documentos/${fileName}`);
      } catch (error) {
        console.error('Erro ao salvar arquivo no mobile:', error);
        alert('Erro ao exportar arquivo: ' + error.message);
      }
    } else {
      XLSX.writeFile(wb, fileName);
    }
  }

  // Métricas principais (sem admins)
  const totalAtivos = useMemo(() => {
    return new Set(eventosFiltrados.map(ev => ev.user_id).filter(Boolean)).size;
  }, [eventosFiltrados]);

  // Taxa de Retenção
  const taxaRetencao = useMemo(() => {
    const hoje = new Date();
    const seteDiasAtras = new Date(hoje.getTime() - 7 * 24 * 60 * 60 * 1000);

    const usuariosComAcessos = {};
    eventosFiltrados.forEach(ev => {
      if (!ev.user_id) return;
      if (!usuariosComAcessos[ev.user_id]) usuariosComAcessos[ev.user_id] = [];
      usuariosComAcessos[ev.user_id].push(new Date(ev.data_hora));
    });

    let retidos = 0;
    const total = Object.keys(usuariosComAcessos).length;

    Object.values(usuariosComAcessos).forEach(datas => {
      const datasOrdenadas = datas.sort((a, b) => a - b);
      const primeiraData = datasOrdenadas[0];
      
      if (primeiraData < seteDiasAtras && datasOrdenadas.length > 1) {
        const voltou = datasOrdenadas.some(d => d > primeiraData && d >= seteDiasAtras);
        if (voltou) retidos++;
      }
    });

    return total > 0 ? Math.round((retidos / total) * 100) : 0;
  }, [eventosFiltrados]);

  // Receita Estimada (MRR)
  const receitaEstimada = useMemo(() => {
    let mrr = 0;
    const agora = new Date();
    usuarios.filter(u => !u.is_admin).forEach(u => {
      if (u.plano_trial) return;
      const expPlano = u.plano_data_expiracao ? new Date(u.plano_data_expiracao) : null;
      const planoAtivo = expPlano && expPlano > agora;
      if (planoAtivo && REVENUE_PLAN_IDS.includes(u.plano)) {
        const catalogPlan = PAYMENT_CATALOG[u.plano];
        mrr += u.plano_periodicidade === 'anual' ? catalogPlan.annual / 12 : catalogPlan.monthly;
      }
    });
    return mrr;
  }, [usuarios]);

  const receitaBreakdown = useMemo(() => {
    const agora = new Date();
    const planos = Object.fromEntries(REVENUE_PLAN_IDS.map(id => [id, { mensal: 0, anual: 0 }]));
    let addon = 0, satelite = 0, expirados = 0;
    usuarios.filter(u => !u.is_admin).forEach(u => {
      if (u.plano_trial) return;
      const expPlano = u.plano_data_expiracao ? new Date(u.plano_data_expiracao) : null;
      const planoAtivo = expPlano && expPlano > agora;
      const planoComercial = REVENUE_PLAN_IDS.includes(u.plano);
      const planoExpirado = expPlano && expPlano <= agora && planoComercial;
      if (planoAtivo && planoComercial) {
        const periodicidade = u.plano_periodicidade === 'anual' ? 'anual' : 'mensal';
        planos[u.plano][periodicidade]++;
      }
      if (planoExpirado) expirados++;
      const TRIAL_DURACAO_MS = 14 * 24 * 60 * 60 * 1000 + 60_000;
      const expAddon = u.addon_irrigacao_expiracao ? new Date(u.addon_irrigacao_expiracao) : null;
      const addonEmTrial = u.addon_irrigacao_trial_usado && u.addon_irrigacao_inicio && expAddon &&
        (expAddon - new Date(u.addon_irrigacao_inicio)) <= TRIAL_DURACAO_MS;
      if (u.addon_irrigacao && expAddon && expAddon > agora && !addonEmTrial) addon++;
      const expSatelite = u.addon_satelite_expiracao ? new Date(u.addon_satelite_expiracao) : null;
      const sateliteEmTrial = u.addon_satelite_trial_usado && u.addon_satelite_inicio && expSatelite &&
        (expSatelite - new Date(u.addon_satelite_inicio)) <= TRIAL_DURACAO_MS;
      if (u.addon_satelite && expSatelite && expSatelite > agora && !sateliteEmTrial) satelite++;
    });
    return { planos, addon, satelite, expirados };
  }, [usuarios]);

  // Novos cadastros (últimas 48h)
  const novosUsuarios = useMemo(() => {
    const limite = new Date(Date.now() - 48 * 60 * 60 * 1000);
    return usuarios
      .filter(u => !u.is_admin && new Date(u.criado_em) >= limite)
      .sort((a, b) => new Date(b.criado_em) - new Date(a.criado_em));
  }, [usuarios]);

  // Add-ons expirando em 7 dias
  const addonsExpirando = useMemo(() => {
    const agora = new Date();
    const em7dias = new Date(agora.getTime() + 7 * 24 * 60 * 60 * 1000);
    const lista = [];
    usuarios.filter(u => !u.is_admin).forEach(u => {
      const expI = u.addon_irrigacao_expiracao ? new Date(u.addon_irrigacao_expiracao) : null;
      if (u.addon_irrigacao && expI && expI > agora && expI <= em7dias)
        lista.push({ _key: u.id + '_i', nome: u.nome, email: u.email, addon: '💧 Irrigação', expiracao: expI, cor: '#0284c7' });
      const expS = u.addon_satelite_expiracao ? new Date(u.addon_satelite_expiracao) : null;
      if (u.addon_satelite && expS && expS > agora && expS <= em7dias)
        lista.push({ _key: u.id + '_s', nome: u.nome, email: u.email, addon: '🛰️ Satélite', expiracao: expS, cor: '#5e35b1' });
    });
    return lista.sort((a, b) => a.expiracao - b.expiracao);
  }, [usuarios]);

  // Distribuição de versões do app
  const distribuicaoVersoes = useMemo(() => {
    const dist = {};
    Object.values(versoesPorUsuario).forEach(({ versao }) => {
      dist[versao] = (dist[versao] || 0) + 1;
    });
    return Object.entries(dist)
      .map(([versao, total]) => ({ versao, total, atual: versao === VERSAO_ATUAL }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 6);
  }, [versoesPorUsuario]);

  // Planos expirando em 7 dias
  const planosExpirando = useMemo(() => {
    const agora = new Date();
    const em7dias = new Date(agora.getTime() + 7 * 24 * 60 * 60 * 1000);
    return usuarios.filter(u => {
      if (u.is_admin) return false;
      if (!u.plano_data_expiracao || u.plano === 'starter') return false;
      const exp = new Date(u.plano_data_expiracao);
      return exp > agora && exp <= em7dias;
    }).sort((a, b) => new Date(a.plano_data_expiracao) - new Date(b.plano_data_expiracao));
  }, [usuarios]);

  // Carregar dados (com proteção contra race condition)
  useEffect(() => {
    let cancelled = false;

    async function fetchData() {
      setLoadingDados(true);
      setEventos([]); // limpa dados antigos imediatamente para evitar flash de dados obsoletos

      let { data: usuariosData, error: usuariosError } = await supabase
        .from('usuarios')
        .select('id, nome, email, is_admin, is_owner, account_status, blocked_at, blocked_reason, criado_em, plano, plano_trial, plano_data_inicio, plano_data_expiracao, plano_periodicidade, plano_trial_usado, addon_irrigacao, addon_irrigacao_expiracao, addon_irrigacao_trial_usado, addon_irrigacao_inicio, addon_satelite, addon_satelite_expiracao, addon_satelite_trial_usado, addon_satelite_inicio')
        .order('criado_em', { ascending: false });

      // Compatibilidade temporária enquanto a migração ainda não foi aplicada
      // ao ambiente que está servindo a interface local.
      if (usuariosError && (['42703', 'PGRST204'].includes(usuariosError.code) || /is_owner|account_status|blocked_/i.test(usuariosError.message || ''))) {
        const legacyResult = await supabase
          .from('usuarios')
          .select('id, nome, email, is_admin, criado_em, plano, plano_trial, plano_data_inicio, plano_data_expiracao, plano_periodicidade, plano_trial_usado, addon_irrigacao, addon_irrigacao_expiracao, addon_irrigacao_trial_usado, addon_irrigacao_inicio, addon_satelite, addon_satelite_expiracao, addon_satelite_trial_usado, addon_satelite_inicio')
          .order('criado_em', { ascending: false });
        usuariosData = (legacyResult.data || []).map(item => ({
          ...item,
          is_owner: false,
          account_status: 'active',
        }));
        usuariosError = legacyResult.error;
      }

      if (cancelled) return;
      setUsuarios(usuariosError ? [] : (usuariosData || []));

      let eventosData = [];

      // Paginação para TODOS os modos — o Supabase limita 1000 linhas por request
      const batchSize = 1000;
      let offset = 0;

      if (filtroPeriodo !== 'todos') {
        const dias = parseInt(filtroPeriodo);
        const dataLimite = new Date();
        dataLimite.setDate(dataLimite.getDate() - dias);
        const isoLimite = dataLimite.toISOString();

        while (true) {
          const { data: batch } = await supabase
            .from('user_events')
            .select('*')
            .gte('data_hora', isoLimite)
            .order('data_hora', { ascending: false })
            .range(offset, offset + batchSize - 1);
          if (!batch || batch.length === 0) break;
          eventosData = [...eventosData, ...batch];
          if (batch.length < batchSize) break; // última página
          offset += batchSize;
        }
      } else {
        // "Todo o período": paginação idêntica, sem filtro de data
        while (eventosData.length < 100000) {
          const { data: batch } = await supabase
            .from('user_events')
            .select('*')
            .order('data_hora', { ascending: false })
            .range(offset, offset + batchSize - 1);
          if (!batch || batch.length === 0) break;
          eventosData = [...eventosData, ...batch];
          if (batch.length < batchSize) break;
          offset += batchSize;
        }
      }

      if (cancelled) return;
      setEventos(eventosData);
      setLoadingDados(false);
    }

    fetchData();

    return () => { cancelled = true; };
  }, [filtroPeriodo]);

  // Filtrar usuários
  const usuariosFiltrados = useMemo(() => {
    return usuarios.filter(u => {
      // Filtro de busca
      if (buscaUsuario) {
        const busca = buscaUsuario.toLowerCase();
        const nome = (u.nome || '').toLowerCase();
        const email = (u.email || '').toLowerCase();
        if (!nome.includes(busca) && !email.includes(busca)) return false;
      }
      
      // Filtro de status
      if (filtroStatus === "ativos" && u.account_status === 'blocked') return false;
      if (filtroStatus === "bloqueados" && u.account_status !== 'blocked') return false;
      
      // Filtro de admin
      if (filtroAdmin === "admin" && !u.is_admin) return false;
      if (filtroAdmin === "nao-admin" && u.is_admin) return false;
      
      // Filtro de plano
      if (filtroPlano !== "todos") {
        if (filtroPlano === "trial" && !u.plano_trial) return false;
        if (filtroPlano !== "trial" && u.plano !== filtroPlano) return false;
      }

      return true;
    }).sort((a, b) => {
      // Ordenar por último acesso
      const acessoA = ultimosAcessos[a.id] ? new Date(ultimosAcessos[a.id]) : new Date(0);
      const acessoB = ultimosAcessos[b.id] ? new Date(ultimosAcessos[b.id]) : new Date(0);
      return acessoB - acessoA;
    });
  }, [usuarios, buscaUsuario, filtroStatus, filtroAdmin, filtroPlano, ultimosAcessos]);

  // Paginação
  const usuariosPaginados = usuariosFiltrados.slice((paginaUsuarios - 1) * itensPorPagina, paginaUsuarios * itensPorPagina);
  const totalPaginasUsuarios = Math.ceil(usuariosFiltrados.length / itensPorPagina);

  // Últimos acessos (somente contas de clientes)
  const ultimosAcessosLista = useMemo(() => (
    usuariosNaoAdmin
      .filter(u => ultimosAcessos[u.id])
      .map(u => ({
        id: u.id,
        nome: u.nome,
        email: u.email,
        plano: u.plano,
        trial: Boolean(u.plano_trial),
        versao: versoesPorUsuario[u.id]?.versao || null,
        ultimoAcesso: ultimosAcessos[u.id],
      }))
      .sort((a, b) => new Date(b.ultimoAcesso) - new Date(a.ultimoAcesso))
      .slice(0, 8)
  ), [usuariosNaoAdmin, ultimosAcessos, versoesPorUsuario]);

  // Novos cadastros por dia (7/30 dias) ou por mês (todo o período)
  const cadastrosSerie = useMemo(() => {
    const cadastros = usuariosNaoAdmin
      .filter(u => u.criado_em && filtrarPorPeriodo(u.criado_em))
      .map(u => new Date(u.criado_em));
    const porMes = filtroPeriodo === 'todos';
    const labels = [];
    const values = [];
    const hoje = new Date();

    if (porMes) {
      if (cadastros.length === 0) return { labels, values, total: 0, porMes };
      const inicio = new Date(Math.min(...cadastros.map(d => d.getTime())));
      const cursor = new Date(inicio.getFullYear(), inicio.getMonth(), 1);
      while (cursor <= hoje) {
        const ano = cursor.getFullYear();
        const mes = cursor.getMonth();
        labels.push(`${String(mes + 1).padStart(2, '0')}/${String(ano).slice(2)}`);
        values.push(cadastros.filter(d => d.getFullYear() === ano && d.getMonth() === mes).length);
        cursor.setMonth(cursor.getMonth() + 1);
      }
    } else {
      const dias = parseInt(filtroPeriodo);
      for (let i = dias - 1; i >= 0; i--) {
        const dia = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - i);
        labels.push(`${dia.getDate()}/${dia.getMonth() + 1}`);
        values.push(cadastros.filter(d => (
          d.getFullYear() === dia.getFullYear() && d.getMonth() === dia.getMonth() && d.getDate() === dia.getDate()
        )).length);
      }
    }
    return { labels, values, total: cadastros.length, porMes };
  }, [usuariosNaoAdmin, filtroPeriodo, dataLimitePeriodo]);

  return (
    <div className="admin-panel-shell">
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* ===== HEADER PERSISTENTE ===== */}
      <header className="admin-panel-header">
        <button type="button" className="admin-panel-back" onClick={onBack} aria-label="Voltar ao dashboard">
          <FaArrowLeft />
        </button>
        <div className="admin-panel-brand">
          <span><FaShieldAlt /></span>
          <div>
            <small>Gestão da plataforma</small>
            <h1>Painel Admin</h1>
            <p>Operação, usuários, planos e acessos em um só lugar</p>
          </div>
        </div>
        <div className="admin-panel-actions">
          <label className="admin-period-select">
            <FaCalendarAlt />
            <select value={filtroPeriodo} onChange={(e) => setFiltroPeriodo(e.target.value)} aria-label="Período das métricas">
              <option value="7">Últimos 7 dias</option>
              <option value="30">Últimos 30 dias</option>
              <option value="todos">Todo período</option>
            </select>
          </label>
          {loadingDados && (
            <span className="admin-loading-status">
              <span />
              Carregando...
            </span>
          )}
          <button type="button" className="admin-panel-exit" onClick={onBack}>
            <FaSignOutAlt /> <span>Sair</span>
          </button>
        </div>
      </header>

      {/* ===== TAB NAVIGATION ===== */}
      <nav className="admin-panel-tabs" aria-label="Áreas administrativas">
        {[
          { key: 'dashboard', label: 'Visão geral', icon: <FaTachometerAlt />, badge: 0 },
          { key: 'usuarios', label: 'Usuários', icon: <FaUsers />, badge: novosUsuarios.length },
          { key: 'uso', label: 'Uso e engajamento', icon: <FaChartPie />, badge: 0 },
          { key: 'planos', label: 'Planos', icon: <FaCrown />, badge: 0 },
          { key: 'permissoesModulos', label: 'Permissões', icon: <FaKey />, badge: 0 },
        ].map(tab => (
          <button
            type="button"
            key={tab.key}
            onClick={() => setAba(tab.key)}
            className={aba === tab.key ? 'is-active' : ''}
            aria-current={aba === tab.key ? 'page' : undefined}
          >
            <span className="admin-tab-icon">{tab.icon}</span>
            <span>{tab.label}</span>
            {tab.badge > 0 && <span className="admin-tab-badge">{tab.badge}</span>}
          </button>
        ))}
      </nav>

      {/* Visão geral */}
      {aba === 'dashboard' && (
        <AdminVisaoGeral
          loading={loadingDados}
          labelPeriodo={labelPeriodo}
          contas={usuariosNaoAdmin.length}
          admins={usuarios.length - usuariosNaoAdmin.length}
          ativos={totalAtivos}
          retencao={taxaRetencao}
          receita={receitaEstimada}
          receitaBreakdown={receitaBreakdown}
          cadastros={cadastrosSerie}
          estatisticasPlanos={estatisticasPlanos}
          novosUsuarios={novosUsuarios}
          planosExpirando={planosExpirando}
          addonsExpirando={addonsExpirando}
          ultimosAcessos={ultimosAcessosLista}
          versoes={distribuicaoVersoes}
          versaoAtual={VERSAO_ATUAL}
          onNavigate={setAba}
        />
      )}
      {/* ===== ABA USO E ENGAJAMENTO ===== */}
      {aba === 'uso' && (
        <AdminUsoAnalytics
          eventos={eventosFiltrados}
          usuarios={usuariosNaoAdmin}
          periodDays={filtroPeriodo === 'todos' ? null : parseInt(filtroPeriodo)}
          labelPeriodo={labelPeriodo}
          loading={loadingDados}
          onExport={exportarUsoUsuarios}
        />
      )}

      {/* ===== ABA USUÁRIOS ===== */}
      {aba === 'usuarios' && (
        <main className="admin-panel-content admin-users-content">
          <div className="admin-section-heading">
            <div><span>Contas e acessos</span><h2>Usuários ({usuariosFiltrados.length})</h2></div>
            <p>
              {currentUserIsOwner
                ? 'Proprietário: gerencia planos, testes, administradores e o acesso das contas.'
                : 'Administrador: gerencia planos e testes. Tornar administradores e bloquear contas são ações exclusivas do proprietário.'}
            </p>
          </div>

          <section className="admin-card">
          <div className="admin-filters">
            <input
              type="text"
              placeholder="Buscar por nome ou e-mail"
              value={buscaUsuario}
              onChange={(e) => { setBuscaUsuario(e.target.value); setPaginaUsuarios(1); }}
            />
            <select value={filtroStatus} onChange={(e) => { setFiltroStatus(e.target.value); setPaginaUsuarios(1); }} aria-label="Status do acesso">
              <option value="todos">Todos os status</option>
              <option value="ativos">Acesso liberado</option>
              <option value="bloqueados">Acesso bloqueado</option>
            </select>
            <select value={filtroAdmin} onChange={(e) => { setFiltroAdmin(e.target.value); setPaginaUsuarios(1); }} aria-label="Tipo de conta">
              <option value="todos">Todos os tipos</option>
              <option value="admin">Administradores</option>
              <option value="nao-admin">Clientes</option>
            </select>
            <select value={filtroPlano} onChange={(e) => { setFiltroPlano(e.target.value); setPaginaUsuarios(1); }} aria-label="Plano">
              <option value="todos">Todos os planos</option>
              <option value="sensor_free">{planLabel('sensor_free')}</option>
              <option value="starter">Starter (teste)</option>
              <option value="essential">{planLabel('essential')}</option>
              <option value="professional">{planLabel('professional')}</option>
              <option value="management">{planLabel('management')}</option>
              <option value="trial">Em período de teste</option>
            </select>
            <button type="button" className="admin-btn" onClick={exportarUsuarios}><FaDownload /> Exportar</button>
          </div>

          <div className="admin-table-wrap">
            <table className="admin-table" style={{ minWidth: 900 }}>
              <thead>
                <tr>
                  <th className="is-left">Usuário</th>
                  <th>Plano</th>
                  <th>Expira</th>
                  <th>Acesso</th>
                  <th>Versão</th>
                  <th>Último acesso</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {usuariosPaginados.length === 0 && (
                  <tr><td colSpan={7} className="admin-empty">Nenhum usuário encontrado.</td></tr>
                )}
                {usuariosPaginados.map(u => {
                  const ultimoAcesso = ultimosAcessos[u.id];
                  const isBlocked = u.account_status === 'blocked';
                  const isOwner = u.is_owner === true;
                  const ownerPlanLocked = isOwner && String(u.id) !== String(currentUser?.id);
                  const expirando = u.plano_data_expiracao && new Date(u.plano_data_expiracao) <= new Date(Date.now() + 7 * 86400000) && new Date(u.plano_data_expiracao) > new Date();
                  const adminLocked = loadingId === u.id || !currentUserIsOwner || isOwner;
                  return (
                    <tr key={u.id}>
                      <td className="is-left">
                        <strong>{u.nome || 'Sem nome'}</strong>
                        <small>{u.email}</small>
                        {isOwner && <span className="admin-pill is-info">Proprietário</span>}
                        {u.is_admin && !isOwner && <span className="admin-pill is-info">Administrador</span>}
                      </td>
                      <td>
                        <span className="admin-pill">{planLabel(u.plano)}</span>
                        {u.plano_trial && <small>Em teste</small>}
                      </td>
                      <td>
                        {u.plano_data_expiracao
                          ? <span className={expirando ? 'admin-pill is-warn' : ''}>{new Date(u.plano_data_expiracao).toLocaleDateString('pt-BR')}</span>
                          : '—'}
                      </td>
                      <td>
                        {isBlocked
                          ? <span className="admin-pill is-danger" title={u.blocked_reason || 'Bloqueio administrativo'}>Bloqueado</span>
                          : <span className="admin-pill is-ok">Liberado</span>}
                      </td>
                      <td>
                        {versoesPorUsuario[u.id]
                          ? <span className={`admin-pill ${versoesPorUsuario[u.id].versao === VERSAO_ATUAL ? 'is-ok' : 'is-warn'}`}>v{versoesPorUsuario[u.id].versao}</span>
                          : '-'}
                      </td>
                      <td>{ultimoAcesso ? new Date(ultimoAcesso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '-'}</td>
                      <td>
                        <div className="admin-actions">
                          <button type="button" className="admin-btn is-sm" onClick={() => { setUsuarioPlanoSelecionado(u); setAba('planos'); }} disabled={loadingId === u.id || ownerPlanLocked} title={ownerPlanLocked ? 'Apenas o proprietário pode renovar o próprio plano' : isOwner ? 'Renovar o próprio plano' : 'Alterar plano ou estender teste grátis'}>
                            Plano / teste
                          </button>
                          <button type="button" className="admin-btn is-sm is-ghost" onClick={() => toggleAdmin(u.id, u.is_admin)} disabled={adminLocked} title={!currentUserIsOwner ? 'Somente o proprietário altera administradores' : isOwner ? 'Conta proprietária protegida' : 'Alterar administrador'}>
                            {loadingId === u.id ? '...' : (u.is_admin ? 'Remover admin' : 'Tornar admin')}
                          </button>
                          <button type="button" className={`admin-btn is-sm ${isBlocked ? 'is-ghost' : 'is-danger'}`} onClick={() => handleToggleAccountAccess(u)} disabled={adminLocked} title={isOwner ? 'Conta proprietária protegida' : isBlocked ? 'Reativar acesso' : 'Bloquear acesso'}>
                            {isBlocked ? 'Reativar' : 'Bloquear'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPaginasUsuarios > 1 && (
            <div className="admin-pager">
              {Array.from({ length: Math.min(totalPaginasUsuarios, 10) }, (_, i) => i + 1).map(p => (
                <button type="button" key={p} className={`admin-btn is-sm ${paginaUsuarios === p ? '' : 'is-ghost'}`} onClick={() => setPaginaUsuarios(p)}>{p}</button>
              ))}
              {totalPaginasUsuarios > 10 && <span className="admin-empty">... {totalPaginasUsuarios} páginas</span>}
            </div>
          )}
          </section>
        </main>
      )}

      {/* Aba de Gestão de Planos */}
      {aba === 'planos' && (
        <main className="admin-panel-content admin-plans-content">
          <div className="admin-section-heading">
            <div><span>Assinaturas e add-ons</span><h2>Gestão de planos</h2></div>
            <p>Selecione um usuário para alterar o plano. Em contas em avaliação, é possível acrescentar 7, 15 ou 30 dias.</p>
          </div>
          
          <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
            {/* Lista de usuários */}
            <div style={{ minWidth: 280, maxWidth: 350, flex: 1 }}>
              <h3 style={{ fontWeight: 700, fontSize: 18, marginBottom: 12, color: '#333' }}>
                Usuários ({usuarios.length})
              </h3>
              
              <input 
                type="text"
                placeholder="🔍 Buscar usuário..."
                value={buscaUsuario}
                onChange={(e) => setBuscaUsuario(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '2px solid #e0e0e0',
                  borderRadius: 8,
                  marginBottom: 16,
                  outline: 'none',
                  fontSize: 14
                }}
              />
              
              <div style={{ maxHeight: 600, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 8 }}>
                {usuarios.filter(u => {
                  if (!buscaUsuario) return true;
                  const busca = buscaUsuario.toLowerCase();
                  return (u.nome?.toLowerCase().includes(busca) || u.email?.toLowerCase().includes(busca));
                }).map(u => {
                  const planoIcon = u.plano === 'pro' ? <FaRocket style={{ color: '#9C27B0' }} /> 
                    : u.plano === 'premium' ? <FaStar style={{ color: '#FFD700' }} /> 
                    : null;
                  
                  return (
                    <div
                      key={u.id}
                      onClick={() => setUsuarioPlanoSelecionado(u)}
                      style={{
                        padding: '12px 16px',
                        borderBottom: '1px solid #e0e0e0',
                        cursor: 'pointer',
                        background: usuarioPlanoSelecionado?.id === u.id ? '#e3f2fd' : 'transparent',
                        transition: 'background 0.2s'
                      }}
                    >
                      <div style={{ fontWeight: 600, fontSize: 14, color: '#333', display: 'flex', alignItems: 'center', gap: 6 }}>
                        {planoIcon}
                        {u.nome || 'Sem nome'} {u.is_owner && <span title="Proprietário da plataforma">👑</span>}
                      </div>
                      <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>{u.email}</div>
                      <div style={{ fontSize: 11, color: '#999', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                        Plano: <strong>{u.plano || 'starter'}</strong>
                        {u.plano_trial && <span style={{ background: '#4caf50', color: '#fff', padding: '2px 6px', borderRadius: 4, fontSize: 10 }}>TRIAL</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Painel de gerenciamento do usuário selecionado */}
            <div style={{ flex: 2, minWidth: 300 }}>
              {usuarioPlanoSelecionado ? (
                <>
                  {/* Informações do usuário */}
                  <div style={{ background: '#f5f5f5', borderRadius: 12, padding: 20, marginBottom: 24 }}>
                    <h3 style={{ margin: '0 0 16px 0', fontSize: 20, fontWeight: 700, color: '#333' }}>
                      {usuarioPlanoSelecionado.nome} {usuarioPlanoSelecionado.is_owner && '👑'}
                    </h3>
                    <div style={{ fontSize: 14, color: '#666', marginBottom: 8 }}>
                      📧 {usuarioPlanoSelecionado.email}
                    </div>
                    <div style={{ fontSize: 14, color: '#666', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                      💎 Plano Atual: <strong style={{ color: '#1976d2' }}>{usuarioPlanoSelecionado.plano || 'starter'}</strong>
                      {usuarioPlanoSelecionado.plano_trial && (
                        <span style={{ background: '#4caf50', color: '#fff', padding: '4px 8px', borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
                          TRIAL
                        </span>
                      )}
                    </div>
                    {usuarioPlanoSelecionado.plano_data_inicio && (
                      <div style={{ fontSize: 13, color: '#999' }}>
                        📅 Início: {new Date(usuarioPlanoSelecionado.plano_data_inicio).toLocaleDateString('pt-BR')}
                      </div>
                    )}
                    {usuarioPlanoSelecionado.plano_data_expiracao && (
                      <div style={{ fontSize: 13, color: '#ff5722', fontWeight: 600 }}>
                        ⏰ Expira: {new Date(usuarioPlanoSelecionado.plano_data_expiracao).toLocaleDateString('pt-BR')}
                      </div>
                    )}
                    {usuarioPlanoSelecionado.is_owner && (
                      <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: '#dff8f3', color: '#08766b', fontSize: 13, fontWeight: 700 }}>
                        Conta proprietária protegida contra troca de plano, bloqueio, rebaixamento e exclusão. A renovação do plano atual continua disponível para o próprio titular.
                      </div>
                    )}
                    {!usuarioPlanoSelecionado.is_owner && usuarioPlanoSelecionado.plano_trial && (
                      <div style={{ marginTop: 14 }}>
                        <div style={{ fontSize: 12, color: '#586f7f', fontWeight: 700, marginBottom: 7 }}>Estender teste grátis</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          {[7, 15, 30].map(days => (
                            <button
                              key={days}
                              type="button"
                              onClick={() => handleExtendTrial(usuarioPlanoSelecionado.id, days)}
                              disabled={loadingPlano}
                              style={{ border: '1px solid #168673', borderRadius: 7, background: '#fff', color: '#08766b', padding: '7px 11px', fontWeight: 700, cursor: loadingPlano ? 'wait' : 'pointer' }}
                            >
                              +{days} dias
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Ações de mudança de plano */}
                  <div style={{ background: '#fff', border: '2px solid #e0e0e0', borderRadius: 12, padding: 20, marginBottom: 24 }}>
                    <h4 style={{ margin: '0 0 16px 0', fontSize: 16, fontWeight: 700, color: '#333' }}>
                      {usuarioPlanoSelecionado.is_owner ? 'Renovar meu plano' : '🔄 Alterar Plano'}
                    </h4>

                    {String(usuarioPlanoSelecionado.id) === String(currentUser?.id) && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
                        {['mensal', 'anual'].map(periodicidade => (
                          <button
                            key={periodicidade}
                            type="button"
                            onClick={() => handleRenewOwnPlan(periodicidade)}
                            disabled={loadingPlano || usuarioPlanoSelecionado.plano === 'sensor_free'}
                            style={{ border: '1px solid #168673', borderRadius: 8, background: '#fff', color: '#08766b', padding: '12px', fontWeight: 700, cursor: loadingPlano ? 'wait' : 'pointer' }}
                          >
                            Renovar {periodicidade === 'mensal' ? 'por 1 mês' : 'por 1 ano'}
                          </button>
                        ))}
                      </div>
                    )}
                    {!usuarioPlanoSelecionado.is_owner && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {/* Starter Trial */}
                      <button
                        onClick={() => handleChangePlan(usuarioPlanoSelecionado.id, 'starter', 30)}
                        disabled={loadingPlano || (usuarioPlanoSelecionado.plano === 'starter' && usuarioPlanoSelecionado.plano_trial)}
                        style={{
                          padding: '16px',
                          border: '2px solid #64B5F6',
                          borderRadius: 10,
                          background: usuarioPlanoSelecionado.plano === 'starter' && usuarioPlanoSelecionado.plano_trial ? '#e3f2fd' : '#fff',
                          cursor: usuarioPlanoSelecionado.plano === 'starter' && usuarioPlanoSelecionado.plano_trial ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          opacity: usuarioPlanoSelecionado.plano === 'starter' && usuarioPlanoSelecionado.plano_trial ? 0.6 : 1
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 16, color: '#1976d2' }}>⏳ Starter Trial</div>
                          <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>Avaliação de 30 dias, uma única vez</div>
                        </div>
                        {usuarioPlanoSelecionado.plano === 'starter' && usuarioPlanoSelecionado.plano_trial && (
                          <FaCheck style={{ color: '#4caf50', fontSize: 20 }} />
                        )}
                      </button>

                      {/* Gratuito Sensor: plano vitalício atribuído somente pelo administrador. */}
                      <button
                        onClick={() => handleChangePlan(usuarioPlanoSelecionado.id, 'sensor_free')}
                        disabled={loadingPlano || usuarioPlanoSelecionado.plano === 'sensor_free'}
                        style={{
                          padding: '16px',
                          border: '2px solid #0f9f8f',
                          borderRadius: 10,
                          background: usuarioPlanoSelecionado.plano === 'sensor_free' ? '#dff8f3' : '#fff',
                          cursor: usuarioPlanoSelecionado.plano === 'sensor_free' ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          opacity: usuarioPlanoSelecionado.plano === 'sensor_free' ? 0.7 : 1
                        }}
                      >
                        <div style={{ textAlign: 'left' }}>
                          <div style={{ fontWeight: 700, fontSize: 16, color: '#08766b' }}>🌦️ Gratuito Sensor</div>
                          <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>Vitalício · somente Sensores · 1 estação</div>
                        </div>
                        {usuarioPlanoSelecionado.plano === 'sensor_free' && (
                          <FaCheck style={{ color: '#0f9f8f', fontSize: 20 }} />
                        )}
                      </button>

                      {NEW_COMMERCIAL_PLAN_IDS.map(planId => {
                        const plan = PAYMENT_CATALOG[planId];
                        const isCurrent = usuarioPlanoSelecionado.plano === planId;
                        return (
                          <div key={planId} style={{ border: `2px solid ${plan.color}`, borderRadius: 10, overflow: 'hidden' }}>
                            <div style={{ padding: '12px 16px', background: isCurrent ? `${plan.color}14` : '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div>
                                <div style={{ fontWeight: 700, fontSize: 16, color: plan.color }}>{plan.icon} {plan.name}</div>
                                <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>{plan.subtitle}</div>
                              </div>
                              {isCurrent && <FaCheck style={{ color: '#4caf50', fontSize: 20 }} />}
                            </div>
                            <div style={{ display: 'flex', borderTop: `1px solid ${plan.color}` }}>
                              {['mensal', 'anual'].map(periodicidade => {
                                const selected = isCurrent && usuarioPlanoSelecionado.plano_periodicidade === periodicidade;
                                const price = periodicidade === 'anual' ? plan.annual : plan.monthly;
                                return (
                                  <button
                                    key={periodicidade}
                                    onClick={() => handleChangePlan(usuarioPlanoSelecionado.id, planId, null, periodicidade)}
                                    disabled={loadingPlano || selected}
                                    style={{ flex: 1, padding: '10px', border: 'none', borderRight: periodicidade === 'mensal' ? `1px solid ${plan.color}` : 'none', background: '#fff', cursor: selected ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, color: plan.color, opacity: selected ? 0.5 : 1 }}
                                  >
                                    {periodicidade === 'mensal' ? '💳 Mensal' : '💰 Anual'}<br />
                                    <span style={{ fontSize: 11 }}>R$ {price.toFixed(2)}/{periodicidade === 'anual' ? 'ano' : 'mês'}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}

                      <div hidden aria-hidden="true" style={{ display: 'none' }}>
                        Premium e Pro abaixo são planos legados. Mantenha-os apenas para clientes que já os possuem.
                      </div>

                      {/* Premium legado */}
                      <div hidden aria-hidden="true" style={{ display: 'none' }}>
                        <div style={{
                          padding: '12px 16px',
                          background: usuarioPlanoSelecionado.plano === 'premium' ? '#FFF9E6' : '#fff',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: 16, color: '#FFA000', display: 'flex', alignItems: 'center', gap: 6 }}>
                              <FaStar /> Premium
                            </div>
                            <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>Escolha a periodicidade</div>
                          </div>
                          {usuarioPlanoSelecionado.plano === 'premium' && (
                            <FaCheck style={{ color: '#4caf50', fontSize: 20 }} />
                          )}
                        </div>
                        <div style={{ display: 'flex', borderTop: '1px solid #FFD700' }}>
                          <button
                            onClick={() => handleChangePlan(usuarioPlanoSelecionado.id, 'premium', null, 'mensal')}
                            disabled={loadingPlano || usuarioPlanoSelecionado.plano !== 'premium' || usuarioPlanoSelecionado.plano_periodicidade === 'mensal'}
                            style={{
                              flex: 1,
                              padding: '10px',
                              border: 'none',
                              borderRight: '1px solid #FFD700',
                              background: '#fff',
                              cursor: 'pointer',
                              fontSize: 13,
                              fontWeight: 600,
                              color: '#FFA000',
                              opacity: (usuarioPlanoSelecionado.plano !== 'premium' || usuarioPlanoSelecionado.plano_periodicidade === 'mensal') ? 0.5 : 1
                            }}
                          >
                            💳 Mensal<br/><span style={{ fontSize: 11 }}>R$ {PAYMENT_CATALOG.premium.monthly.toFixed(2)}/mês</span>
                          </button>
                          <button
                            onClick={() => handleChangePlan(usuarioPlanoSelecionado.id, 'premium', null, 'anual')}
                            disabled={loadingPlano || usuarioPlanoSelecionado.plano !== 'premium' || usuarioPlanoSelecionado.plano_periodicidade === 'anual'}
                            style={{
                              flex: 1,
                              padding: '10px',
                              border: 'none',
                              background: '#fff',
                              cursor: 'pointer',
                              fontSize: 13,
                              fontWeight: 600,
                              color: '#FFA000',
                              opacity: (usuarioPlanoSelecionado.plano !== 'premium' || usuarioPlanoSelecionado.plano_periodicidade === 'anual') ? 0.5 : 1
                            }}
                          >
                            💰 Anual<br/><span style={{ fontSize: 11 }}>R$ {PAYMENT_CATALOG.premium.annual.toFixed(2)}/ano</span>
                          </button>
                        </div>
                      </div>

                      {/* Pro legado */}
                      <div hidden aria-hidden="true" style={{ display: 'none' }}>
                        <div style={{
                          padding: '12px 16px',
                          background: usuarioPlanoSelecionado.plano === 'pro' ? '#F3E5F5' : '#fff',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: 16, color: '#9C27B0', display: 'flex', alignItems: 'center', gap: 6 }}>
                              <FaRocket /> Pro
                            </div>
                            <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>Escolha a periodicidade</div>
                          </div>
                          {usuarioPlanoSelecionado.plano === 'pro' && (
                            <FaCheck style={{ color: '#4caf50', fontSize: 20 }} />
                          )}
                        </div>
                        <div style={{ display: 'flex', borderTop: '1px solid #9C27B0' }}>
                          <button
                            onClick={() => handleChangePlan(usuarioPlanoSelecionado.id, 'pro', null, 'mensal')}
                            disabled={loadingPlano || usuarioPlanoSelecionado.plano !== 'pro' || usuarioPlanoSelecionado.plano_periodicidade === 'mensal'}
                            style={{
                              flex: 1,
                              padding: '10px',
                              border: 'none',
                              borderRight: '1px solid #9C27B0',
                              background: '#fff',
                              cursor: 'pointer',
                              fontSize: 13,
                              fontWeight: 600,
                              color: '#9C27B0',
                              opacity: (usuarioPlanoSelecionado.plano !== 'pro' || usuarioPlanoSelecionado.plano_periodicidade === 'mensal') ? 0.5 : 1
                            }}
                          >
                            💳 Mensal<br/><span style={{ fontSize: 11 }}>R$ {PAYMENT_CATALOG.pro.monthly.toFixed(2)}/mês</span>
                          </button>
                          <button
                            onClick={() => handleChangePlan(usuarioPlanoSelecionado.id, 'pro', null, 'anual')}
                            disabled={loadingPlano || usuarioPlanoSelecionado.plano !== 'pro' || usuarioPlanoSelecionado.plano_periodicidade === 'anual'}
                            style={{
                              flex: 1,
                              padding: '10px',
                              border: 'none',
                              background: '#fff',
                              cursor: 'pointer',
                              fontSize: 13,
                              fontWeight: 600,
                              color: '#9C27B0',
                              opacity: (usuarioPlanoSelecionado.plano !== 'pro' || usuarioPlanoSelecionado.plano_periodicidade === 'anual') ? 0.5 : 1
                            }}
                          >
                            💰 Anual<br/><span style={{ fontSize: 11 }}>R$ {PAYMENT_CATALOG.pro.annual.toFixed(2)}/ano</span>
                          </button>
                        </div>
                      </div>

                    </div>
                    )}
                  </div>

                  {/* Add-on Irrigação */}
                  <div hidden aria-hidden="true" style={{ display: 'none' }}>
                    <h4 style={{ margin: '0 0 16px 0', fontSize: 16, fontWeight: 700, color: '#1565C0', display: 'flex', alignItems: 'center', gap: 8 }}>
                      💧 Add-on: Manejo de Irrigação
                    </h4>

                    {irrigacaoInfo ? (
                      <div style={{ marginBottom: 16, padding: 12, borderRadius: 8, background: irrigacaoInfo.ativo ? '#E3F2FD' : irrigacaoInfo.expirado ? '#FFF3E0' : '#f5f5f5' }}>
                        <div style={{ fontWeight: 600, color: irrigacaoInfo.ativo ? '#1565C0' : irrigacaoInfo.expirado ? '#E65100' : '#666' }}>
                          {irrigacaoInfo.ativo
                            ? `✅ Ativo — expira em ${irrigacaoInfo.expiracao?.toLocaleDateString('pt-BR')}`
                            : irrigacaoInfo.expirado
                              ? `⚠️ Expirado em ${irrigacaoInfo.expiracao?.toLocaleDateString('pt-BR')}`
                              : '🔒 Nunca ativado'}
                        </div>
                        {irrigacaoInfo.trialUsado && (
                          <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>Trial de 14 dias já utilizado</div>
                        )}
                      </div>
                    ) : (
                      <div style={{ color: '#999', marginBottom: 16 }}>Carregando...</div>
                    )}

                    {/* Botões de duração */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                      {[
                        { label: '30 dias', dias: 30 },
                        { label: '90 dias', dias: 90 },
                        { label: '6 meses', dias: 180 },
                        { label: '1 ano', dias: 365 },
                        { label: '2 anos', dias: 730 },
                      ].map(op => (
                        <button
                          key={op.dias}
                          onClick={() => {
                            if (window.confirm(`Ativar Add-on Irrigação por ${op.label} para ${usuarioPlanoSelecionado.nome || usuarioPlanoSelecionado.email}?`)) {
                              handleToggleAddonIrrigacao(true, op.dias);
                            }
                          }}
                          disabled={loadingIrrigacao}
                          style={{
                            padding: '8px 16px',
                            border: '2px solid #2196F3',
                            borderRadius: 8,
                            background: '#E3F2FD',
                            color: '#1565C0',
                            fontWeight: 600,
                            cursor: 'pointer',
                            fontSize: 13
                          }}
                        >
                          💧 Ativar {op.label}
                        </button>
                      ))}
                    </div>

                    {irrigacaoInfo?.ativo && (
                      <button
                        onClick={() => {
                          if (window.confirm(`Desativar Add-on Irrigação para ${usuarioPlanoSelecionado.nome || usuarioPlanoSelecionado.email}?`)) {
                            handleToggleAddonIrrigacao(false);
                          }
                        }}
                        disabled={loadingIrrigacao}
                        style={{
                          padding: '8px 16px',
                          border: '2px solid #f44336',
                          borderRadius: 8,
                          background: '#FFEBEE',
                          color: '#c62828',
                          fontWeight: 600,
                          cursor: 'pointer',
                          fontSize: 13
                        }}
                      >
                        🚫 Revogar acesso
                      </button>
                    )}
                  </div>

                  {/* Add-on Satélite */}
                  <div hidden aria-hidden="true" style={{ display: 'none' }}>
                    <h4 style={{ margin: '0 0 4px 0', fontSize: 16, fontWeight: 700, color: '#4A148C', display: 'flex', alignItems: 'center', gap: 8 }}>
                      🛰️ Add-on: Sensoriamento por Satélite
                    </h4>
                    <div style={{ fontSize: 12, color: '#7B1FA2', marginBottom: 12, fontWeight: 600 }}>R$ {PAYMENT_CATALOG.addon_satelite.monthly.toFixed(2)}/mês</div>

                    {sateliteInfo ? (
                      <div style={{ marginBottom: 16, padding: 12, borderRadius: 8, background: sateliteInfo.ativo ? '#F3E5F5' : sateliteInfo.expirado ? '#FFF3E0' : '#f5f5f5' }}>
                        <div style={{ fontWeight: 600, color: sateliteInfo.ativo ? '#4A148C' : sateliteInfo.expirado ? '#E65100' : '#666' }}>
                          {sateliteInfo.ativo
                            ? `✅ Ativo — expira em ${sateliteInfo.expiracao?.toLocaleDateString('pt-BR')}`
                            : sateliteInfo.expirado
                              ? `⚠️ Expirado em ${sateliteInfo.expiracao?.toLocaleDateString('pt-BR')}`
                              : '🔒 Nunca ativado'}
                        </div>
                        {sateliteInfo.trialUsado && (
                          <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>Trial já utilizado</div>
                        )}
                      </div>
                    ) : (
                      <div style={{ color: '#999', marginBottom: 16 }}>Carregando...</div>
                    )}

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                      {[
                        { label: '30 dias', dias: 30 },
                        { label: '90 dias', dias: 90 },
                        { label: '6 meses', dias: 180 },
                        { label: '1 ano', dias: 365 },
                        { label: '2 anos', dias: 730 },
                      ].map(op => (
                        <button
                          key={op.dias}
                          onClick={() => {
                            if (window.confirm(`Ativar Add-on Satélite por ${op.label} para ${usuarioPlanoSelecionado.nome || usuarioPlanoSelecionado.email}?`)) {
                              handleToggleAddonSatelite(true, op.dias);
                            }
                          }}
                          disabled={loadingSatelite}
                          style={{
                            padding: '8px 16px',
                            border: '2px solid #7B1FA2',
                            borderRadius: 8,
                            background: '#F3E5F5',
                            color: '#4A148C',
                            fontWeight: 600,
                            cursor: 'pointer',
                            fontSize: 13
                          }}
                        >
                          🛰️ Ativar {op.label}
                        </button>
                      ))}
                    </div>

                    {sateliteInfo?.ativo && (
                      <button
                        onClick={() => {
                          if (window.confirm(`Desativar Add-on Satélite para ${usuarioPlanoSelecionado.nome || usuarioPlanoSelecionado.email}?`)) {
                            handleToggleAddonSatelite(false);
                          }
                        }}
                        disabled={loadingSatelite}
                        style={{
                          padding: '8px 16px',
                          border: '2px solid #f44336',
                          borderRadius: 8,
                          background: '#FFEBEE',
                          color: '#c62828',
                          fontWeight: 600,
                          cursor: 'pointer',
                          fontSize: 13
                        }}
                      >
                        🚫 Revogar acesso
                      </button>
                    )}
                  </div>

                  {/* Histórico de mudanças */}
                  {historicoPlanos.length > 0 && (
                    <div style={{ background: '#fff', border: '2px solid #e0e0e0', borderRadius: 12, padding: 20 }}>
                      <h4 style={{ margin: '0 0 16px 0', fontSize: 16, fontWeight: 700, color: '#333' }}>
                        📜 Histórico de Mudanças
                      </h4>
                      <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                        {historicoPlanos.map((item, idx) => (
                          <div key={idx} style={{
                            padding: '12px',
                            borderBottom: idx < historicoPlanos.length - 1 ? '1px solid #e0e0e0' : 'none',
                            fontSize: 13
                          }}>
                            <div style={{ fontWeight: 600, color: '#333' }}>
                              {item.plano_anterior ? `${item.plano_anterior} → ` : ''}{item.plano_novo}
                            </div>
                            <div style={{ color: '#999', fontSize: 12, marginTop: 4 }}>
                              {new Date(item.data_mudanca).toLocaleString('pt-BR')}
                            </div>
                            {item.motivo && (
                              <div style={{ color: '#666', fontSize: 12, marginTop: 4, fontStyle: 'italic' }}>
                                {item.motivo}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: 60,
                  color: '#999',
                  background: '#f5f5f5',
                  borderRadius: 8
                }}>
                  <FaCrown size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
                  <div style={{ fontSize: 16 }}>Selecione um usuário para gerenciar o plano</div>
                </div>
              )}
            </div>
          </div>
        </main>
      )}

      {/* Aba de Permissões de Módulos */}
      {aba === 'permissoesModulos' && (
        <main className="admin-panel-content admin-permissions-content">
          <div className="admin-section-heading">
            <div><span>Acesso por módulo</span><h2>Permissões de módulos</h2></div>
          </div>
          
          <div className="admin-permissions-layout">
            {/* Lista de usuários */}
            <div className="admin-permissions-users">
              <h3 style={{ fontWeight: 700, fontSize: 18, marginBottom: 12, color: '#333' }}>
                Contas gerenciáveis ({usuarios.length})
              </h3>
              
              <input 
                type="text"
                placeholder="🔍 Buscar usuário..."
                value={buscaUsuario}
                onChange={(e) => setBuscaUsuario(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '2px solid #e0e0e0',
                  borderRadius: 8,
                  marginBottom: 16,
                  outline: 'none',
                  fontSize: 14
                }}
              />

              <select
                className="admin-permissions-mobile-select"
                aria-label="Selecionar usuário para gerenciar permissões"
                value={usuarioSelecionado?.id || ''}
                onChange={event => setUsuarioSelecionado(
                  usuarios.find(item => String(item.id) === event.target.value) || null
                )}
              >
                <option value="">Selecione uma conta</option>
                {usuariosFiltradosPermissoes.map(u => (
                  <option key={u.id} value={u.id}>{u.nome || 'Sem nome'} — {u.email}</option>
                ))}
              </select>
              
              <div className="admin-permissions-user-list">
                {usuariosFiltradosPermissoes.map(u => (
                  <div
                    key={u.id}
                    onClick={() => setUsuarioSelecionado(u)}
                    style={{
                      padding: '12px 16px',
                      cursor: 'pointer',
                      background: usuarioSelecionado?.id === u.id ? '#e3f2fd' : '#fff',
                      borderBottom: '1px solid #f0f0f0',
                      transition: 'background 0.2s'
                    }}
                  >
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{u.nome || 'Sem nome'}</div>
                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>{u.email}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Permissões do usuário selecionado */}
            <div className="admin-permissions-editor">
              {usuarioSelecionado ? (
                <>
                  <div style={{ background: '#f5f5f5', padding: 16, borderRadius: 8, marginBottom: 20 }}>
                    <div style={{ fontWeight: 700, fontSize: 18, color: '#1976d2' }}>
                      {usuarioSelecionado.nome || 'Sem nome'}
                    </div>
                    <div style={{ fontSize: 14, color: '#666', marginTop: 4 }}>
                      {usuarioSelecionado.email}
                    </div>
                    {usuarioSelecionado.is_owner && (
                      <div style={{ marginTop: 8, color: '#08766b', fontSize: 12, fontWeight: 800 }}>
                        👑 Proprietário do software — acesso integral protegido
                      </div>
                    )}
                  </div>
                  
                  <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: '#333' }}>
                    Módulos Permitidos
                  </h3>
                  {usuarioSelecionado.plano === 'sensor_free' && (
                    <div style={{ padding: 12, marginBottom: 16, borderRadius: 8, background: '#dff8f3', color: '#08766b', fontSize: 13, fontWeight: 600 }}>
                      O plano Gratuito Sensor libera somente Sensores. Para liberar outros módulos, altere o plano da conta.
                    </div>
                  )}
                  {usuarioSelecionado.plano !== 'sensor_free' && getPlanIncludedModules(usuarioSelecionado.plano).length > 0 && (
                    <div style={{ padding: 12, marginBottom: 16, borderRadius: 8, background: '#e8f2ff', color: '#145c9e', fontSize: 13, fontWeight: 600 }}>
                      O plano {PAYMENT_CATALOG[usuarioSelecionado.plano]?.name || usuarioSelecionado.plano} inclui os módulos abaixo. Desative um item somente para criar uma exceção individual para este usuário.
                    </div>
                  )}
                  
                  {loadingPerm ? (
                    <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>Carregando...</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {modulosDisponiveis.map(modulo => {
                        const sensorPlanLocked = usuarioSelecionado.plano === 'sensor_free';
                        const ownerLocked = usuarioSelecionado.is_owner === true;
                        const includedInPlan = isModuleIncludedInPlan(modulo.nome, usuarioSelecionado.plano);
                        const isChecked = ownerLocked || isModuleAllowed(modulo.nome, userModulos, false, usuarioSelecionado.plano);
                        return (
                          <label
                            key={modulo.nome}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 12,
                              padding: 16,
                              background: isChecked ? '#e8f5e9' : '#fff',
                              border: `2px solid ${isChecked ? '#4caf50' : '#e0e0e0'}`,
                              borderRadius: 8,
                              cursor: sensorPlanLocked || ownerLocked ? 'not-allowed' : 'pointer',
                              opacity: (sensorPlanLocked && modulo.nome !== 'sensores') || ownerLocked ? 0.6 : 1,
                              transition: 'all 0.2s'
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              disabled={sensorPlanLocked || ownerLocked}
                              onChange={(e) => handleToggleModulo(modulo.nome, e.target.checked)}
                              style={{ width: 20, height: 20, cursor: sensorPlanLocked || ownerLocked ? 'not-allowed' : 'pointer' }}
                            />
                            <span style={{ flex: 1, minWidth: 0, fontSize: 16, fontWeight: 600, color: isChecked ? '#2e7d32' : '#333' }}>
                              {modulo.label}
                              {includedInPlan && !ownerLocked && (
                                <small style={{ display: 'block', marginTop: 3, color: '#287362', fontSize: 11, fontWeight: 700 }}>Incluído no plano</small>
                              )}
                            </span>
                            {isChecked && <FaCheck color="#4caf50" style={{ marginLeft: 'auto', flexShrink: 0 }} />}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: 60,
                  color: '#999',
                  background: '#f5f5f5',
                  borderRadius: 8
                }}>
                  <FaUser size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
                  <div style={{ fontSize: 16 }}>Selecione um usuário para gerenciar permissões</div>
                </div>
              )}
            </div>
          </div>
        </main>
      )}
    </div>
  );
}

export default AdminPanel;
